// The engine as the UI uses it: the Wasm worker when it is available, the
// TypeScript fallback (no highlighting) when it fails to load. Every call
// has a time budget (see BUDGET_MS).
import type { EngineApi } from '../engine/types';
import { engineWith, engineFailed, engineReady, prioritize, type CallOptions } from '../engine/client';
import { fallbackEngine } from './fallback';
import { degraded } from './degraded.svelte';

type Async<T> = { [K in keyof T]: T[K] extends (...a: infer A) => infer R ? (...a: A) => Promise<R> : never };

let broken = false;
/** The engine is unavailable from now on (see `engineDegraded`). */
function degrade() {
  broken = true;
  degraded.value = true;
}
let ready: Promise<boolean> | null = null;

/**
 * Milliseconds a call may run in the worker before it is rejected and the
 * worker replaced (the worker runs one call at a time and cannot interrupt
 * one, so a slow call would hold up every later one). Time queued behind
 * other calls does not count. Callers treat a rejected `highlight` as plain
 * text and a rejected `detectMoves` as no moves; a rejected `blocks` is
 * tried again plainer (fileBlocks.ts). `blocks` is a plain computation's
 * budget (`BlocksInput.plain`); a full one has `FULL_BLOCKS_MS`.
 */
export const BUDGET_MS: Record<keyof EngineApi, number> = {
  languages: 5_000,
  detectLanguage: 5_000,
  highlight: 5_000,
  symbols: 5_000,
  parsePatch: 10_000,
  blocks: 20_000,
  lineMap: 10_000,
  resolveLegacy: 5_000,
  detectMoves: 15_000,
  patchComplexity: 10_000,
  attention: 5_000,
};

/**
 * A full `blocks` computation's budget: the first part of the 30 s a
 * file's diff may take, the plain diff it falls back to having the rest
 * (fileBlocks.ts). A file slow in its extras (a parse, alignment) shows
 * its plain diff after this, not after the whole 30 s, and holds the
 * worker no longer.
 */
export const FULL_BLOCKS_MS = 10_000;

/**
 * Resolves true once the engine has loaded, false once it cannot. While it
 * is still loading (a 15 MB module on a slow link) calls wait for it rather
 * than switching to the fallback, whose keys would turn review marks off.
 */
function whenReady(): Promise<boolean> {
  ready ??= engineReady().then(
    () => true,
    (e) => {
      degrade();
      console.warn('engine unavailable; using the TypeScript fallback', e);
      return false;
    },
  );
  return ready;
}

/**
 * True once the Wasm engine is unavailable (it failed to load, or a
 * replacement worker failed to load later) and the fallback is in use. A
 * call that fails while the engine is available is an error, not a reason
 * to degrade: the client restarts a crashed worker itself. Fallback block
 * keys are never stored as review marks (see diff/keys.ts). Reactive: a
 * template or effect reading it follows the change.
 */
export function engineDegraded(): boolean {
  return degraded.value;
}

function wrap<K extends keyof EngineApi>(name: K, options: CallOptions = {}, timeoutMs = BUDGET_MS[name]) {
  const wasm = engineWith({ ...options, timeoutMs })[name] as unknown as (...a: unknown[]) => Promise<unknown>;
  return async (...args: Parameters<EngineApi[K]>): Promise<ReturnType<EngineApi[K]>> => {
    if (!broken && (await whenReady())) {
      try {
        return (await wasm(...args)) as ReturnType<EngineApi[K]>;
      } catch (e) {
        if (!engineFailed()) throw e;
        degrade();
        console.warn('engine became unavailable; using the TypeScript fallback', e);
      }
    }
    return (fallbackEngine[name] as (...a: unknown[]) => ReturnType<EngineApi[K]>)(...args);
  };
}

const plainBlocks = wrap('blocks');
const fullBlocks = wrap('blocks', {}, FULL_BLOCKS_MS);

export const eng: Async<EngineApi> = {
  languages: wrap('languages'),
  detectLanguage: wrap('detectLanguage'),
  highlight: wrap('highlight'),
  symbols: wrap('symbols'),
  parsePatch: wrap('parsePatch'),
  blocks: (input) => (input.plain ? plainBlocks : fullBlocks)(input),
  lineMap: wrap('lineMap'),
  resolveLegacy: wrap('resolveLegacy'),
  detectMoves: wrap('detectMoves'),
  patchComplexity: wrap('patchComplexity'),
  attention: wrap('attention'),
};

/** `eng.highlight` for the file the reader has open: sent to the worker
 *  before highlights held for prefetched files. */
export const urgentHighlight = wrap('highlight', { urgent: true });

/** Sends a highlight already queued for this text next (the reader opened a
 *  file whose highlight prefetch had queued). */
export function prioritizeHighlight(language: string, text: string): void {
  prioritize('highlight', [language, text]);
}
